import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { tenantsApi } from '../api/tenantsApi';
import { useToast } from '../../../shared/components/toast/useToast';
import type { CreateTenantPayload } from '../types';

const errMsg = (err: unknown, fallback: string) => {
  const msg = (err as { message?: unknown })?.message;
  if (Array.isArray(msg)) return (msg as string[])[0] || fallback;
  return (typeof msg === 'string' ? msg : '') || fallback;
};

export const useTenants = () =>
  useQuery({ queryKey: ['tenants'], queryFn: tenantsApi.list, staleTime: 2 * 60 * 1000 });

export const useTenant = (id: string) =>
  useQuery({ queryKey: ['tenants', id], queryFn: () => tenantsApi.get(id), staleTime: 2 * 60 * 1000, enabled: !!id });

export const useCreateTenant = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: (data: CreateTenantPayload) => tenantsApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenants'] });
      toast.success('Tenant created — the admin has been emailed an invite');
    },
    onError: (err: unknown) => toast.error(errMsg(err, 'Failed to create tenant')),
  });
};

export const useToggleTenantStatus = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) =>
      active ? tenantsApi.deactivate(id) : tenantsApi.activate(id),
    onSuccess: (_, { active }) => {
      queryClient.invalidateQueries({ queryKey: ['tenants'] });
      toast.success(`Tenant ${active ? 'deactivated' : 'activated'} successfully`);
    },
    onError: (err: unknown) => toast.error(errMsg(err, 'Failed to update tenant status')),
  });
};

export const useGenerateTenantApiKey = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: (id: string) => tenantsApi.generateApiKey(id),
    onSuccess: () => {
      // hasApiKey is derived on the tenant rows — refresh list and any open detail.
      queryClient.invalidateQueries({ queryKey: ['tenants'] });
    },
    onError: (err: unknown) => toast.error(errMsg(err, 'Failed to generate API key')),
  });
};
